import { create } from 'zustand';
import { App } from '@capacitor/app';

import {
  bindMediaSessionHandlers,
  setMediaSessionPlaybackState,
  updateMediaSessionMetadata,
  type MediaSessionMeta,
} from '@/utils/mediaSession';
import type { DiscoverTrack, Track } from '@/types';

/**
 * Audio preview player — web port of the legacy `useAudioPlayer` (expo-av).
 * A single shared HTMLAudioElement plays one 30s preview at a time.
 *
 * Stale-link recovery: if the source fails to load (old/expired Deezer preview),
 * the element's `error` event triggers the registered `refresh` callback, which
 * asks the backend to re-resolve + persist a fresh link, then retries once.
 *
 * F4 — every claim on the player (play / toggle / stop) bumps a monotonic
 * `generation`. Async continuations capture it and check it before touching
 * state or `src`: swiping through five cards in two seconds used to leave the
 * player fighting itself — a rejected `play()` from card 1 flipped `isPlaying`
 * off while card 5 was audibly playing, and a slow stale-link `refresh()` for
 * card 2 assigned its URL over card 5's. Latest claim always wins.
 */
let audio: HTMLAudioElement | null = null;

/**
 * F4 — is the async work started at `generation` (for track `id`) still the
 * player's current business? Any newer claim invalidates it.
 */
function isCurrent(generation: number, id: string | null): boolean {
  const state = useAudioPlayer.getState();
  return state.generation === generation && state.currentId === id;
}

/**
 * F4 — start playback and apply the outcome ONLY if still current.
 *
 * `AbortError` is the browser telling us the play() was superseded by a pause or
 * a `src` reassignment — that is the normal outcome of a fast swipe, not a
 * failure, so it must never flip `isPlaying`.
 */
function startPlayback(element: HTMLAudioElement, generation: number, id: string | null): void {
  void element.play().catch((error: unknown) => {
    if (!isCurrent(generation, id)) return;
    if (error instanceof DOMException && error.name === 'AbortError') return;
    useAudioPlayer.setState({ isPlaying: false });
  });
}

/**
 * playback-progress-ring — the elapsed share of the current preview, kept OUT of
 * the zustand state on purpose: every screen reads `useAudioPlayer()` without a
 * selector, so a position written there ~4 times a second would re-render the
 * whole feed card, result list or impact list. Only the indicator subscribes here.
 */
let progress = 0;
/** The current preview ran to its end: the indicator is gone until it plays again. */
let ended = false;
const progressListeners = new Set<() => void>();

function updateProgress(element: HTMLAudioElement): void {
  const { currentTime, duration } = element;
  progress = Number.isFinite(duration) && duration > 0 ? Math.min(currentTime / duration, 1) : 0;
  for (const listener of progressListeners) listener();
}

function setEnded(value: boolean, element: HTMLAudioElement): void {
  ended = value;
  updateProgress(element);
}

/**
 * A new sound takes the player: its indicator starts from 0 now. Waiting for the
 * element's `emptied` / `play` events (queued by the browser) would let the new
 * sound show the previous one's position for a frame, since `currentId` changes
 * at once.
 */
function resetProgress(): void {
  progress = 0;
  ended = false;
  for (const listener of progressListeners) listener();
}

/** Elapsed share of the current preview, 0–1 (0 while its duration is unknown). */
export const getPlaybackProgress = (): number => progress;

/** Did the current preview run to its end (and not play since)? */
export const isPlaybackEnded = (): boolean => ended;

/** Be told at each position change; returns the unsubscribe function. */
export const subscribePlaybackProgress = (listener: () => void): (() => void) => {
  progressListeners.add(listener);
  return () => {
    progressListeners.delete(listener);
  };
};

function getAudio(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio();
    const element = audio;
    // `emptied` fires when a new `src` starts loading: the position falls back to 0
    // before the next sound's first `timeupdate`, never showing the old one's.
    for (const type of ['timeupdate', 'loadedmetadata', 'seeked', 'emptied']) {
      element.addEventListener(type, () => updateProgress(element));
    }
    element.addEventListener('ended', () => setEnded(true, element));
    element.addEventListener('play', () => setEnded(false, element));
    audio.addEventListener('ended', () => {
      useAudioPlayer.setState({ isPlaying: false });
      setMediaSessionPlaybackState('paused');
    });
    audio.addEventListener('pause', () => {
      useAudioPlayer.setState({ isPlaying: false });
      setMediaSessionPlaybackState('paused');
    });
    audio.addEventListener('play', () => {
      useAudioPlayer.setState({ isPlaying: true });
      setMediaSessionPlaybackState('playing');
    });
    // F3 — lock-screen play/pause drive the same shared element (iOS shell only).
    bindMediaSessionHandlers({
      play: () => {
        if (!audio) return;
        const { generation, currentId } = useAudioPlayer.getState();
        startPlayback(audio, generation, currentId);
      },
      pause: () => audio?.pause(),
    });
    // feed-position-and-pause — leaving the app (home button, another app, the
    // lock screen) pauses the preview: it is for discovering, not background
    // listening. PAUSE, not stop — a tap resumes where it was — and nothing on
    // the way back: a sound starting by itself surprises. Registered once, with
    // the element; `@capacitor/app` maps this to `visibilitychange` on the web.
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) audio?.pause();
    }).catch(() => {
      // Best effort: without the listener a preview keeps playing in the
      // background, which is the old behaviour — never a reason to break playback.
    });
    audio.addEventListener('error', () => {
      const { refresh, retried, generation, currentId } = useAudioPlayer.getState();
      if (!refresh || retried) return;
      // F4 — bind the retry to the track that actually errored. `refresh()` is a
      // network round-trip to re-resolve a dead Deezer link; by the time it
      // answers the user may be three cards further down, and assigning its URL
      // would yank the player back to a track nobody is looking at.
      const myGeneration = generation;
      const myId = currentId;
      useAudioPlayer.setState({ retried: true });
      void refresh()
        .then((url) => {
          if (!isCurrent(myGeneration, myId) || !audio) return;
          if (!url) return;
          audio.pause();
          audio.src = url;
          startPlayback(audio, myGeneration, myId);
        })
        .catch(() => {
          if (!isCurrent(myGeneration, myId)) return;
          useAudioPlayer.setState({ isPlaying: false });
        });
    });
  }
  return audio;
}

interface AudioPlayerState {
  currentId: string | null;
  isPlaying: boolean;
  refresh: (() => Promise<string | undefined>) | null;
  retried: boolean;
  /**
   * F4 — monotonic claim counter. Every play/toggle/stop bumps it, which
   * invalidates any async continuation captured under an earlier value.
   */
  generation: number;
  /** Play this preview if not already current; pause if it is. */
  toggle: (
    url: string | undefined,
    id: string,
    refresh?: () => Promise<string | undefined>,
    meta?: MediaSessionMeta,
  ) => void;
  /** Play this preview (switch to it if needed), never pausing — used for autoplay. */
  play: (
    url: string | undefined,
    id: string,
    refresh?: () => Promise<string | undefined>,
    meta?: MediaSessionMeta,
  ) => void;
  stop: () => void;
}

export const useAudioPlayer = create<AudioPlayerState>((set, get) => ({
  currentId: null,
  isPlaying: false,
  refresh: null,
  retried: false,
  generation: 0,
  toggle: (url, id, refresh, meta) => {
    if (!url) return;
    const a = getAudio();
    // Tapping the card that is already playing: just pause. No new claim — the
    // `pause` listener flips `isPlaying`, and the track stays current so a
    // second tap resumes where it left off.
    if (get().currentId === id && get().isPlaying) {
      a.pause();
      return;
    }
    const generation = get().generation + 1;
    if (get().currentId !== id) {
      // Pause before swapping `src`: an un-paused element rejects its pending
      // play() with AbortError and, on WebKit, can keep decoding the old buffer.
      a.pause();
      a.src = url;
      resetProgress();
      set({ currentId: id, refresh: refresh ?? null, retried: false, generation });
    } else {
      set({ generation });
    }
    if (meta) updateMediaSessionMetadata(meta);
    startPlayback(a, generation, id);
  },
  play: (url, id, refresh, meta) => {
    if (!url) return;
    const a = getAudio();
    const generation = get().generation + 1;
    if (get().currentId === id) {
      // Already the current track — re-claim it (so a stale continuation from a
      // previous card can't turn it off) and only resume if it isn't running.
      set({ generation });
      if (!get().isPlaying) startPlayback(a, generation, id);
      return;
    }
    a.pause();
    a.src = url;
    resetProgress();
    set({ currentId: id, refresh: refresh ?? null, retried: false, generation });
    if (meta) updateMediaSessionMetadata(meta);
    startPlayback(a, generation, id);
  },
  stop: () => {
    const a = getAudio();
    a.pause();
    a.currentTime = 0;
    setMediaSessionPlaybackState('none');
    // Bumping the generation here is what cancels an in-flight stale-link
    // retry: when its `refresh()` finally resolves, `isCurrent` is false and it
    // never touches `src`.
    set({
      currentId: null,
      isPlaying: false,
      refresh: null,
      retried: false,
      generation: get().generation + 1,
    });
  },
}));

/** Resolve a playable preview URL from either track shape. */
export const getPreviewUrl = (
  track: Track | DiscoverTrack | null | undefined,
): string | undefined => {
  if (!track) return undefined;
  const previewUrl = 'previewUrl' in track ? track.previewUrl : undefined;
  const metadataPreviewUrl = 'metadata' in track ? track.metadata?.preview_url : undefined;
  return previewUrl || metadataPreviewUrl || undefined;
};
